import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Target, Loader2, Phone, CheckCircle2, ThumbsUp, RefreshCw, XCircle, HelpCircle, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

import { IntelligenceSummaryCards } from './advanced/IntelligenceSummaryCards';
import { OutcomeDrillSheet } from './advanced/OutcomeDrillSheet';
import { DateRangePills, formatDaysLabel } from './AnalyticsFilters';
import OpportunityMap from './OpportunityMap';
import SystemHealthCard from './SystemHealthCard';
import GymSalesMatchRateCard from './GymSalesMatchRateCard';
import SmsConversionCard from './SmsConversionCard';
import ReplyInboxCard from './ReplyInboxCard';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const computeExcellentRate = (revenueCorrelation, totalCalls) => {
  if (!totalCalls) return null;
  const excellent = (revenueCorrelation || []).find(r => r.score_range === '80–100');
  if (!excellent?.count) return null;
  return `${Math.round((excellent.count / totalCalls) * 100)}%`;
};

// The 6 top cards on the redesigned "Coaching & Revenue Opportunity Map".
// `outcome_type` is the exact string stored on deal_outcomes.outcome_type —
// used by the drill-down slide-over to fetch the underlying rows. The
// "Calls Analysed" card has no matching outcome_type (it's the total) so
// clicking it is disabled.
const OUTCOME_CARDS = [
  { key: 'total_calls_analysed', label: 'Calls Analysed',  icon: Phone,        color: '#0F172A', bg: 'bg-slate-50 border-slate-200',       outcome_type: null },
  { key: 'closed_deals',         label: 'Closed Deals',    icon: CheckCircle2, color: '#10B981', bg: 'bg-emerald-50 border-emerald-200',   outcome_type: 'Closed Deal' },
  { key: 'likely_closed',        label: 'Likely Closed',   icon: ThumbsUp,     color: '#14B8A6', bg: 'bg-teal-50 border-teal-200',         outcome_type: 'Likely Closed' },
  { key: 'revivable_deals',      label: 'Revivable Opps', icon: RefreshCw,    color: '#F59E0B', bg: 'bg-amber-50 border-amber-200',       outcome_type: 'Revivable Deal' },
  { key: 'lost_deals',           label: 'Lost Deals',      icon: XCircle,      color: '#64748B', bg: 'bg-slate-50 border-slate-200',       outcome_type: 'Lost Deal' },
  { key: 'unclear_outcomes',     label: 'Unclear',         icon: HelpCircle,   color: '#94A3B8', bg: 'bg-slate-50 border-slate-200',       outcome_type: 'Unclear Outcome' },
];

const DealOutcomeCards = ({ summary, onOutcomeClick }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
    {OUTCOME_CARDS.map((c) => {
      const Icon = c.icon;
      const clickable = Boolean(c.outcome_type);
      const count = summary?.[c.key] || 0;
      return (
        <button
          type="button"
          key={c.key}
          onClick={clickable ? () => onOutcomeClick(c) : undefined}
          disabled={!clickable}
          className={`rounded-xl border ${c.bg} p-3 flex flex-col gap-1 text-left transition ${
            clickable
              ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400'
              : 'cursor-default'
          }`}
          data-testid={`deal-card-${c.key}`}
          aria-label={clickable ? `View ${count} ${c.label} calls` : undefined}
        >
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <Icon className="w-3.5 h-3.5" style={{ color: c.color }} />
            {c.label}
          </div>
          <div className="text-2xl font-bold tabular-nums" style={{ color: c.color }}>
            {count.toLocaleString()}
          </div>
          {clickable && count > 0 && (
            <div className="text-[10px] text-slate-400 mt-0.5">Click to view calls →</div>
          )}
        </button>
      );
    })}
  </div>
);

const AdvancedTab = ({ domainId = null, onCriteriaClick = null, onVolumeCellClick = null }) => {
  const { userIndustry } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [oppMap, setOppMap] = useState(null);
  const [outcomeSummary, setOutcomeSummary] = useState(null);
  const [insightsEnabled, setInsightsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [drill, setDrill] = useState({ open: false, outcome_type: null, label: null });

  // The pills emit null (All), a preset number, or 'from:to'. Backend
  // endpoints accept the same string shape via _date_match.
  const daysParam = days == null ? 'all' : String(days);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', daysParam);
    const qs = `?${p}`;

    Promise.allSettled([
      axios.get(`${API}/analytics/intelligence${qs}`),
      axios.get(`${API}/analytics/opportunity-map${qs}&top_n=8`),
      axios.get(`${API}/analytics/deal-outcomes/summary${qs}`),
    ])
      .then(([intel, opp, sum]) => {
        if (cancelled) return;
        if (intel.status === 'fulfilled') setData(intel.value.data);
        else toast.error('Failed to load intelligence data');
        if (opp.status === 'fulfilled') {
          setOppMap(opp.value.data);
          if (opp.value.data?.enabled) setInsightsEnabled(true);
        }
        if (sum.status === 'fulfilled') setOutcomeSummary(sum.value.data);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [domainId, userIndustry, daysParam]);

  const topOpportunity = useMemo(
    () => (oppMap?.map || [])[0] || null,
    [oppMap],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const filtersBar = (
    <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl px-4 py-2.5" data-testid="intelligence-filters">
      <DateRangePills days={days} onChange={setDays} />
      <span className="text-[11px] text-slate-400">{formatDaysLabel(days)}</span>
    </div>
  );

  if (!data?.total_calls) {
    return (
      <div className="space-y-6" data-testid="opportunity-map-tab">
        {filtersBar}
        <div className="py-12 text-center text-slate-500">
          No intelligence data available for {formatDaysLabel(days)}. Try a wider date range or grade some calls first.
        </div>
      </div>
    );
  }

  const { revenue_correlation, total_calls, total_low_score, objection_heatmap } = data;
  const opportunityRows = oppMap?.map || [];

  return (
    <div className="space-y-6" data-testid="opportunity-map-tab">
      {/* Screen title + Export button (visible when outcome data is on) */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Coaching &amp; Revenue Opportunity Map</h2>
          <p className="text-xs text-slate-500 mt-1">
            Prioritised coaching opportunities linked to stronger call outcomes and recoverable deals — {formatDaysLabel(days)}.
          </p>
        </div>
        {insightsEnabled && outcomeSummary?.enabled && (
          <button
            type="button"
            onClick={async () => {
              try {
                const p = new URLSearchParams();
                if (domainId) p.set('domain_id', domainId);
                p.set('days', '60');
                p.set('limit', '2000');
                const r = await axios.get(
                  `${API}/analytics/deal-outcomes/export?${p.toString()}`,
                  { responseType: 'blob' },
                );
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement('a');
                link.href = url;
                const cd = r.headers['content-disposition'] || '';
                const m = cd.match(/filename="([^"]+)"/);
                link.setAttribute('download', m?.[1] || 'deal-outcomes.csv');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('CSV downloaded — 60-day board export.');
              } catch {
                toast.error('Export failed.');
              }
            }}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-slate-900 hover:bg-slate-700 text-white inline-flex items-center gap-1.5 flex-shrink-0"
            data-testid="advanced-export-all-btn"
            title="Download all deal outcomes for the last 60 days as CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        )}
      </div>

      {filtersBar}

      {/* Top 6 outcome cards (visible when AI Insights toggle is on) */}
      {insightsEnabled && outcomeSummary?.enabled ? (
        <>
          <DealOutcomeCards
            summary={outcomeSummary}
            onOutcomeClick={(card) => {
              // Revivable tile jumps to the Revivable Deals tab
              // (the queue no longer lives on this page).
              if (card.key === 'revivable_deals') {
                // Search-only navigation keeps the current dashboard
                // mount path (/my-domain for tenants, / for master).
                navigate({ search: '?tab=revivable' });
                return;
              }
              setDrill({
                open: true,
                outcome_type: card.outcome_type,
                label: card.label,
              });
            }}
          />
          {/* GymSales match-rate KPI — sits directly under the six outcome
              cards so ops can see, at a glance, whether coaching plans are
              being personalised with real CRM history. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <GymSalesMatchRateCard domainId={domainId} days={30} />
            <SmsConversionCard domainId={domainId} days={30} />
          </div>
          <ReplyInboxCard domainId={domainId} days={14} />
        </>
      ) : (
        <IntelligenceSummaryCards
          totalCalls={total_calls}
          totalLowScore={total_low_score}
          topFailure={topOpportunity?.topic || objection_heatmap?.[0]?.topic}
          excellentRate={computeExcellentRate(revenue_correlation, total_calls)}
        />
      )}

      {/* Main opportunity map — uses new payload when insights are on,
          falls back to legacy heatmap wording via the same component. */}
      {opportunityRows.length > 0 ? (
        <OpportunityMap map={opportunityRows} onViewCalls={onCriteriaClick} />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Enable AI Call Insights on this domain to unlock the revenue-linked opportunity map,
          deal outcome breakdown, and revivable-deal queue.
        </div>
      )}

      {/* Coaching → Revenue table moved to My Coaching; heatmap moved to
          Individual; Revivable Deal Queue moved to the Revivable Deals
          tab; Recently Contacted removed (Sep-2026 spec). */}

      {/* Master-admin system health incl. Extension Change Audit. */}
      <SystemHealthCard domainId={domainId} />

      {/* Opportunity Insight — kept (its Score-Outcome Correlation
          neighbour was removed per the Sep-2026 spec). */}
      <Card className="border-teal-100 bg-gradient-to-r from-teal-50 to-cyan-50 shadow-sm">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-teal-800">Opportunity Insight</p>
              <p className="text-xs text-teal-700 mt-1">
                {topOpportunity
                  ? `Your top priority focus is "${topOpportunity.topic}" — ${topOpportunity.demonstrated_pct}% demonstrated, ${topOpportunity.opportunity_pct}% opportunity across ${topOpportunity.total_count} calls. ${topOpportunity.recommended_action}`
                  : 'Not enough graded calls to generate insights. Grade more calls to unlock recommendations.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outcome drill-down slide-over — driven by DealOutcomeCards clicks. */}
      <OutcomeDrillSheet
        open={drill.open}
        onOpenChange={(v) => setDrill((prev) => ({ ...prev, open: v }))}
        outcomeType={drill.outcome_type}
        outcomeLabel={drill.label}
        domainId={domainId}
        days={60}
      />
    </div>
  );
};

export default AdvancedTab;
